import React, { useState, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { promptsAPI } from '../services/api';
import { estimateTokens, formatTokenCount, MAX_PROMPT_LENGTH } from '../utils/tokenCounter';
import './AddPrompt.css';

const PREDEFINED_TAGS = ['Writing', 'Coding', 'Data', 'Business', 'Fun', 'Education', 'Marketing', 'Research', 'Creative', 'Technical'];

function AddPrompt({ currentUsername, showToast }) {
  const [title, setTitle] = useState('');
  const [promptText, setPromptText] = useState('');
  const [category, setCategory] = useState('');
  const [model, setModel] = useState('');
  const [toneStyle, setToneStyle] = useState('');
  const [useCase, setUseCase] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [duplicateWarning, setDuplicateWarning] = useState(null);
  const navigate = useNavigate();

  const tokenEstimate = useMemo(() => {
    return formatTokenCount(estimateTokens(promptText));
  }, [promptText]);

  const handleTagClick = (tag) => {
    const tags = category ? category.split(',').map(t => t.trim()) : [];
    if (tags.includes(tag)) {
      setCategory(tags.filter(t => t !== tag).join(', '));
    } else {
      setCategory([...tags, tag].join(', '));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!currentUsername) {
      setError('Please set your username first');
      showToast('Please set your username first', 'error');
      return;
    }

    setLoading(true);
    setError(null);

    // Frontend validation
    if (promptText.length > MAX_PROMPT_LENGTH) {
      setError(`Prompt text exceeds maximum length of ${MAX_PROMPT_LENGTH} characters`);
      showToast(`Prompt text exceeds maximum length of ${MAX_PROMPT_LENGTH} characters`, 'error');
      setLoading(false);
      return;
    }

    try {
      const response = await promptsAPI.create({
        title,
        prompt_text: promptText,
        category: category.trim(),
        model: model.trim() || null,
        tone_style: toneStyle.trim() || null,
        use_case: useCase.trim() || null,
        username: currentUsername,
      });
      showToast('Prompt created successfully!', 'success');
      navigate('/');
    } catch (err) {
      // Check for duplicate warning
      if (err.response?.status === 409 && err.response?.data?.duplicate) {
        const similarPrompt = err.response.data.similar_prompt;
        setDuplicateWarning(similarPrompt);
        setError(`A similar prompt already exists: "${similarPrompt.title}"`);
        showToast('A similar prompt already exists', 'warning');
      } else {
        const errorMsg = err.response?.data?.error || 'Failed to create prompt';
        setError(errorMsg);
        showToast(errorMsg, 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container">
      <h1>Add New Prompt</h1>
      {error && <div className="error">{error}</div>}
      {duplicateWarning && (
        <div className="duplicate-warning">
          <strong>⚠️ Similar prompt found:</strong>
          <Link to={`/prompts/${duplicateWarning.id}`} className="duplicate-link">
            {duplicateWarning.title}
          </Link>
          <p>Are you sure you want to create a duplicate? You can still proceed if this is intentional.</p>
        </div>
      )}
      <form onSubmit={handleSubmit} className="add-prompt-form">
        <div className="form-group">
          <label className="label">Title</label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="input"
            required
          />
        </div>
        <div className="form-group">
          <label className="label">Prompt Text</label>
          <textarea
            value={promptText}
            onChange={(e) => setPromptText(e.target.value)}
            className="textarea"
            rows={10}
            placeholder="Enter the exact prompt text to use with AI (plain text only)."
            required
          />
          <div className="text-counter">
            <span className={promptText.length > MAX_PROMPT_LENGTH ? 'text-danger' : ''}>
              {promptText.length} / {MAX_PROMPT_LENGTH} characters
            </span>
            <span>{tokenEstimate}</span>
          </div>
        </div>
        <div className="form-group">
          <label className="label">Tags (select or type comma-separated)</label>
          <div className="tag-selector">
            <div className="tag-pills">
              {PREDEFINED_TAGS.map(tag => {
                const tags = category ? category.split(',').map(t => t.trim()) : [];
                const isSelected = tags.includes(tag);
                return (
                  <button
                    key={tag}
                    type="button"
                    onClick={() => handleTagClick(tag)}
                    className={`tag-pill ${isSelected ? 'selected' : ''}`}
                  >
                    {tag}
                  </button>
                );
              })}
            </div>
            <input
              type="text"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="input"
              placeholder="Or type tags separated by commas (e.g., Writing, Business)"
              required
            />
          </div>
        </div>
        <div className="form-group">
          <label className="label">Model (optional)</label>
          <input
            type="text"
            value={model}
            onChange={(e) => setModel(e.target.value)}
            className="input"
            placeholder="e.g., GPT-4, Claude, Gemini"
          />
        </div>
        <div className="form-group">
          <label className="label">Tone / Style (optional)</label>
          <input
            type="text"
            value={toneStyle}
            onChange={(e) => setToneStyle(e.target.value)}
            className="input"
            placeholder="e.g., formal, playful, professional"
          />
        </div>
        <div className="form-group">
          <label className="label">Use Case (optional)</label>
          <input
            type="text"
            value={useCase}
            onChange={(e) => setUseCase(e.target.value)}
            className="input"
            placeholder="e.g., data analysis, email reply, code review"
          />
        </div>
        <button type="submit" className="button button-primary" disabled={loading}>
          {loading ? 'Creating...' : 'Create Prompt'}
        </button>
      </form>
    </div>
  );
}

export default AddPrompt;
